import {
  isMagazineRoute,
  isRegularResearchTopicRoute
} from "~/helpers/magazineHelpers";
import { setCdnCacheControlFor301Redirect } from "~/utils/routeCache";

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.client) return;
  if (!to.name?.toString().includes("research-topics-rtId")) return;

  const cdnCacheEnabled = useEnvBoolean(
    useRuntimeConfig().public.cdnCacheEnabled
  );

  const { rtId, rtSlug } = to.params;
  const researchTopicId = Number(rtId);
  if (isNaN(researchTopicId)) {
    return createError({
      statusCode: 404,
      statusMessage: "Invalid Research Topic ID"
    });
  }

  const { enableMagazine } = useRuntimeConfig();
  const { getResearchTopicBasicInfo } = useMongoDbService();
  const researchTopic = await getResearchTopicBasicInfo(researchTopicId);

  if (researchTopic === null) {
    return createError({
      statusCode: 404,
      statusMessage: `Research Topic ID ${researchTopicId} not found`
    });
  }

  const detailPath = `/research-topics/${researchTopic.ResearchTopicId}/${researchTopic.Slug}`;
  const servesMagazine =
    researchTopic.IsMagazinePage && useEnvBoolean(enableMagazine);

  // Permanent: a magazine Research Topic never goes back to the regular pages.
  if (servesMagazine && isRegularResearchTopicRoute(to)) {
    setCdnCacheControlFor301Redirect(useRequestEvent(), cdnCacheEnabled, 301);
    return navigateTo(
      {
        path: `${detailPath}/magazine`,
        query: to.query
      },
      {
        redirectCode: 301
      }
    );
  }

  if (isMagazineRoute(to)) {
    if (servesMagazine) return;

    // Temporary: a regular Research Topic can still be converted to a magazine.
    return navigateTo(
      {
        path: detailPath,
        query: to.query
      },
      {
        redirectCode: 302
      }
    );
  }

  let redirectionPath!: string;

  if (typeof rtSlug === "string" && rtSlug) {
    const rtPages = ["overview", "articles", "authors", "impact"];

    if (rtPages.includes(rtSlug)) {
      redirectionPath = `/research-topics/${researchTopic.ResearchTopicId}/${
        researchTopic.Slug
      }${rtSlug === "overview" ? "" : "/" + rtSlug}`;
    } else if (
      rtSlug !== researchTopic.Slug ||
      to.name.toString().includes("-overview")
    ) {
      redirectionPath = `/research-topics/${researchTopic.ResearchTopicId}/${researchTopic.Slug}`;
    }
  } else {
    redirectionPath = `/research-topics/${researchTopic.ResearchTopicId}/${researchTopic.Slug}`;
  }

  if (redirectionPath) {
    setCdnCacheControlFor301Redirect(useRequestEvent(), cdnCacheEnabled, 301);
    return navigateTo(
      {
        path: redirectionPath,
        query: to.query
      },
      {
        redirectCode: 301,
        replace: true
      }
    );
  }
});
